import React, { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box } from '@mui/material';
import theme from './theme';
import Navbar from './components/shared/Navbar';
import { ToastProvider } from './components/shared/Toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { DRAWER_WIDTH } from './components/shared/Navbar';
import routeConfig, { resolveRoles } from './config/routes';
import './i18n';
import './App.css';
import BottomNav from './components/shared/BottomNav';
import FeedbackFAB from './components/feedback/FeedbackFAB';
import FeedbackPanel from './components/feedback/FeedbackPanel';
import PrototypeRouteChildren, { PrototypeLayout } from './prototype/PrototypeApp';

// Route-level code splitting: map component name to lazy-loaded component
const COMPONENTS = {
  Dashboard: lazy(() => import('./components/shared/Dashboard')),
  Login: lazy(() => import('./components/shared/Login')),
  Register: lazy(() => import('./pages/Register')),
  IncomingDeliveries: lazy(() => import('./components/incoming/IncomingDeliveries')),
  IncomingStockOverview: lazy(() => import('./components/incoming/IncomingStockOverview')),
  IncomingDeliveryDetail: lazy(() => import('./components/incoming/IncomingDeliveryDetail')),
  ProductionOrders: lazy(() => import('./components/production/ProductionOrders')),
  ProductionOrderForm: lazy(() => import('./components/production/ProductionOrderForm')),
  ProductionPlanning: lazy(() => import('./components/production/ProductionPlanning')),
  MasterDataManagement: lazy(() => import('./components/admin/MasterDataManagement')),
  UserManagement: lazy(() => import('./components/admin/UserManagement')),
  PalletList: lazy(() => import('./components/pallets/PalletList')),
  PalletDetail: lazy(() => import('./components/pallets/PalletDetail')),
  SupplierDocuments: lazy(() => import('./components/admin/SupplierDocuments')),
  UserProfile: lazy(() => import('./components/admin/UserProfile')),
  Help: lazy(() => import('./components/shared/Help')),
  ProductionRecipeList: lazy(() => import('./components/production/ProductionRecipeList')),
  ProductionRecipeForm: lazy(() => import('./components/production/ProductionRecipeForm')),
  ProductionSheetScreen: lazy(() => import('./components/production/ProductionSheetScreen')),
  ShopfloorMobileView: lazy(() => import('./components/production/ShopfloorMobileView')),
  ReceiveGoods: lazy(() => import('./components/production/ReceiveGoods')),
  ProductionStart: lazy(() => import('./components/production/ProductionStart')),
  StockManagement: lazy(() => import('./components/production/StockManagement')),
  PendingApproval: lazy(() => import('./components/shared/PendingApproval')),
  RecallList: lazy(() => import('./components/recalls/RecallList')),
  RecallForm: lazy(() => import('./components/recalls/RecallForm')),
  RecallDetail: lazy(() => import('./components/recalls/RecallDetail')),
  LotTraceGraph: lazy(() => import('./components/recalls/LotTraceGraph')),
  ComplianceDashboard: lazy(() => import('./components/shared/ComplianceDashboard')),
  TraceTestAutoGenerate: lazy(() => import('./components/shared/TraceTestAutoGenerate')),
  OperatorApp: lazy(() => import('./components/operator/OperatorApp')),
  OperatorLogin: lazy(() => import('./components/operator/OperatorLogin')),
  AuditList: lazy(() => import('./components/audits/AuditList')),
  AuditForm: lazy(() => import('./components/audits/AuditForm')),
  AuditDetail: lazy(() => import('./components/audits/AuditDetail')),
  ProcessViewer: lazy(() => import('./components/shared/ProcessViewer')),
};

const MainContent = styled(Box, { shouldForwardProp: (prop) => prop !== 'sidebarOpen' && prop !== 'isMobile' })(
  ({ theme, sidebarOpen, isMobile }) => ({
    flexGrow: 1,
    marginTop: '64px',
    padding: theme.spacing(3),
    transition: theme.transitions.create(['margin-left', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: sidebarOpen && !isMobile ? `${DRAWER_WIDTH}px` : 0,
    width: sidebarOpen && !isMobile ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
  })
);

function PrivateRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    // Not logged in - redirect to login page
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Note: Approval flow removed - all authenticated users can access based on their role
  // Users with role 'user' are treated as basic operators with limited access

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Logged in but not authorized for this route
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="prototype" element={<PrototypeLayout />}>
        {PrototypeRouteChildren}
      </Route>
      {routeConfig.map((route) => {
        const Component = COMPONENTS[route.component];
        if (!Component) return null;
        const element = route.allowedRoles
          ? <PrivateRoute allowedRoles={resolveRoles(route.allowedRoles)}><Component /></PrivateRoute>
          : <Component />;
        return <Route key={route.path} path={route.path} element={element} />;
      })}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const { sidebarOpen, isMobile } = useSidebar();

  // Force Navbar visible (hide for auth pages and for prototype)
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isPrototype = location.pathname.startsWith('/prototype');
  const showNavbar = !isAuthPage && !isPrototype; // Always show navbar if not on auth page or prototype
  const hideFeedbackPages = ['/login', '/register', '/pending', '/operator/login'];
  const showFeedback = user && !hideFeedbackPages.includes(location.pathname);

  return (
    <div className="App">
      {isPrototype ? (
        <React.Suspense fallback={<div className="loading">Loading...</div>}>
          <AppRoutes />
        </React.Suspense>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh' }}>
          {showNavbar && <Navbar />}
          <MainContent
            sidebarOpen={sidebarOpen}
            isMobile={isMobile}
            className={`main-content ${showNavbar ? 'with-navbar' : ''}`}
          >
            {!isAuthPage && (
              <Container maxWidth="xl" sx={{ pl: 0 }}>
                <React.Suspense fallback={<div className="loading">Loading...</div>}>
                  <AppRoutes />
                </React.Suspense>
              </Container>
            )}
            {isAuthPage && (
              <React.Suspense fallback={<div className="loading">Loading...</div>}>
                <AppRoutes />
              </React.Suspense>
            )}
          </MainContent>
        </Box>
      )}
      {showFeedback && (
        <>
          <FeedbackFAB />
          <FeedbackPanel />
        </>
      )}
      {user && !isAuthPage && <BottomNav />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <AuthProvider>
            <SidebarProvider>
              <FeedbackProvider>
                <AppContent />
              </FeedbackProvider>
            </SidebarProvider>
          </AuthProvider>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
